import { Box, Typography, useMediaQuery } from '@mui/material';
import React, {
    useEffect,
    useState,
    useRef,
    useCallback,
    useMemo
} from 'react';
import UserAvatar from '../components/UserAvatar';
import GroupMessageBox from '../components/GroupMessageBox';
import GroupChatDetails from '../components/GroupChatDetails';
import { FaArrowDown } from 'react-icons/fa';
import {
    useGetChatProfileQuery,
    useGetChatGroupQuery,
    useGetGroupMessagesQuery
} from '@store/feature/chat/chatApi';
import SendGroupChat from '../components/SendGroupChat';
import { useParams } from 'react-router-dom';
import { useReadGroupMessageMutation } from '../../../../store/feature/chat/chatApi';
import { FiCommand } from 'react-icons/fi';

const GroupChatMessageContent = () => {
    const { id } = useParams();
    const [before, setBefore] = useState(null);
    const [showChatDetails, setShowChatDetails] = useState(false);
    const [allMessages, setAllMessages] = useState([]);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);

    // Refs
    const bottomRef = useRef(null);
    const messagesWrapperRef = useRef(null);
    const topMessageRef = useRef(null);
    const hasInitiallyScrolled = useRef(false);
    const shouldScrollToBottom = useRef(false);
    const lastPageRef = useRef(null);
    const isNewChatSession = useRef(false); // Track if we just switched chats

    const isSmallScreen = useMediaQuery('(max-width: 1080px)');

    // API hooks
    const { data: profile } = useGetChatProfileQuery();
    const { data: groupProfile, isLoading } = useGetChatGroupQuery(id);
    const [readGroupMessage] = useReadGroupMessageMutation();

    const {
        data: page,
        refetch,
        isFetching
    } = useGetGroupMessagesQuery(
        {
            groupId: id,
            before: before, // Pass the ISO string directly, no conversion needed
            limit: 20
        },
        {
            refetchOnMountOrArgChange: true,
            pollingInterval: 3000,
            skip: !id
        }
    );

    // Simple scroll to bottom function
    const scrollToBottom = useCallback((behavior = 'smooth') => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior });
        }
    }, []);

    // Check if user is at bottom
    const isAtBottom = useCallback(() => {
        if (!messagesWrapperRef.current) return true;
        const { scrollTop, scrollHeight, clientHeight } =
            messagesWrapperRef.current;
        return scrollHeight - scrollTop - clientHeight <= 50;
    }, []);

    // Handle scroll events to show/hide scroll button
    const handleScroll = useCallback(() => {
        const atBottom = isAtBottom();
        setShowScrollButton(!atBottom && allMessages.length > 0);
    }, [isAtBottom, allMessages.length]);

    // Enhanced date formatting with better readability
    const formatDate = useCallback((timestampOrIso) => {
        const date =
            typeof timestampOrIso === 'number'
                ? new Date(timestampOrIso * 1000)
                : new Date(timestampOrIso);

        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const messageDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate()
        );
        const todayDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
        );
        const yesterdayDate = new Date(
            yesterday.getFullYear(),
            yesterday.getMonth(),
            yesterday.getDate()
        );

        if (messageDate.getTime() === todayDate.getTime()) {
            return 'Today';
        } else if (messageDate.getTime() === yesterdayDate.getTime()) {
            return 'Yesterday';
        } else {
            // Check if it's within the current week
            const diffTime = todayDate.getTime() - messageDate.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays <= 7 && diffDays > 0) {
                return date.toLocaleDateString('en-US', { weekday: 'long' });
            } else {
                return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year:
                        messageDate.getFullYear() !== todayDate.getFullYear()
                            ? 'numeric'
                            : undefined
                });
            }
        }
    }, []);

    // Reset chat state when id changes
    const prevIdRef = useRef(id);
    useEffect(() => {
        if (prevIdRef.current && prevIdRef.current !== id) {
            // Reset all chat-related state
            setAllMessages([]);
            setBefore(null);
            setShowChatDetails(false);
            setShowScrollButton(false);
            setIsLoadingOlderMessages(false);
            setHasMoreMessages(true);

            // Reset refs
            hasInitiallyScrolled.current = false;
            shouldScrollToBottom.current = false;
            lastPageRef.current = null;
            isNewChatSession.current = true; // Mark as new chat session

            // Trigger new API call
            refetch();
        }
        prevIdRef.current = id;
    }, [id, refetch]);

    // Handle page data and merge messages
    useEffect(() => {
        if (!page?.items || !Array.isArray(page.items)) {
            setIsLoadingOlderMessages(false);
            return;
        }

        // Handle empty chat case - when there are no messages at all
        if (page.items.length === 0) {
            setIsLoadingOlderMessages(false);
            setHasMoreMessages(false);
            return;
        }

        // Reverse to get ascending order (oldest to newest)
        const ascending = [...page.items].reverse();
        const unseen = ascending.filter(
            (m) => !allMessages.some((msg) => msg.uuid === m.uuid)
        );

        if (!unseen.length) {
            // Reset loading state even if no new messages
            setIsLoadingOlderMessages(false);
            return;
        }

        // Check if we have more messages to load
        const hasMore =
            page.current_page_count === 20 && page.items.length === 20;
        setHasMoreMessages(hasMore);

        if (before === null) {
            // Append new messages (real-time mode)
            setAllMessages((prev) => {
                // Create a map for faster lookup
                const existingIds = new Set(prev?.map((msg) => msg.uuid));
                const newUniqueMessages = unseen.filter(
                    (msg) => !existingIds.has(msg.uuid)
                );

                if (newUniqueMessages.length === 0) return prev;

                const updatedMessages = [...prev, ...newUniqueMessages];

                // If this is a new chat session and we just loaded the first batch of messages
                if (
                    isNewChatSession.current &&
                    prev.length === 0 &&
                    newUniqueMessages.length > 0
                ) {
                    // Force scroll to bottom after DOM update for new chat sessions
                    setTimeout(() => {
                        if (bottomRef.current) {
                            bottomRef.current.scrollIntoView({
                                behavior: 'auto'
                            });
                        }
                    }, 100);

                    // Additional delayed scroll to ensure it works
                    setTimeout(() => {
                        if (bottomRef.current) {
                            bottomRef.current.scrollIntoView({
                                behavior: 'smooth'
                            });
                        }
                    }, 300);

                    isNewChatSession.current = false;
                }

                return updatedMessages;
            });
        } else {
            // Prepend older messages (pagination mode) - maintain scroll position
            const wrapper = messagesWrapperRef.current;
            if (wrapper) {
                const prevHeight = wrapper.scrollHeight;
                setAllMessages((prev) => {
                    // Create a map for faster lookup
                    const existingIds = new Set(prev?.map((msg) => msg.uuid));
                    const newUniqueMessages = unseen.filter(
                        (msg) => !existingIds.has(msg.uuid)
                    );

                    if (newUniqueMessages.length === 0) return prev;

                    return [...newUniqueMessages, ...prev];
                });
                // Maintain scroll position after adding older messages
                requestAnimationFrame(() => {
                    if (wrapper) {
                        wrapper.scrollTop = wrapper.scrollHeight - prevHeight;
                    }
                });
            }
            // Reset loading state after pagination
            setIsLoadingOlderMessages(false);
        }

        // Store the current page reference
        lastPageRef.current = page;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page?.items, before]); // allMessages and page intentionally omitted to prevent infinite loop

    // Auto-scroll logic - FIXED for group chat
    useEffect(() => {
        if (!allMessages.length) return;

        const lastMessage = allMessages[allMessages.length - 1];
        const isOwnMessage = lastMessage.from === profile?.uuid;

        // Always scroll for messages from current user
        if (isOwnMessage) {
            scrollToBottom('smooth');
            return;
        }

        // Improved initial scroll logic
        if (!hasInitiallyScrolled.current) {
            // Add a small delay to ensure DOM is fully rendered
            setTimeout(() => {
                scrollToBottom('auto');
                // Additional scroll attempt with smooth behavior
                setTimeout(() => scrollToBottom('smooth'), 100);
            }, 50);
            hasInitiallyScrolled.current = true;
            return;
        }

        // Force scroll if we explicitly set the flag (for sent messages)
        if (shouldScrollToBottom.current) {
            setTimeout(() => scrollToBottom('smooth'), 100);
            shouldScrollToBottom.current = false;
            return;
        }

        // For messages from other users: only scroll if user is at bottom
        // This prevents unwanted scrolling when user2 is scrolled up and user1 sends a message
        if (isAtBottom()) {
            scrollToBottom('smooth');
        }
    }, [allMessages, scrollToBottom, isAtBottom, profile?.uuid]);

    // Lazy loading for older messages using Intersection Observer
    useEffect(() => {
        if (
            !allMessages.length ||
            !topMessageRef.current ||
            isLoadingOlderMessages ||
            !hasMoreMessages ||
            isFetching
        ) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (
                    entries[0].isIntersecting &&
                    hasMoreMessages &&
                    !isLoadingOlderMessages
                ) {
                    setIsLoadingOlderMessages(true);

                    // Pass the created_at ISO string directly as the before parameter
                    const oldestMessage = allMessages[0];
                    if (oldestMessage) {
                        setBefore(oldestMessage.created_at);
                    }
                }
            },
            {
                threshold: 0.1,
                rootMargin: '20px'
            }
        );

        const currentRef = topMessageRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [allMessages, hasMoreMessages, isLoadingOlderMessages, isFetching]);

    // Set up scroll listener
    useEffect(() => {
        const wrapper = messagesWrapperRef.current;
        if (!wrapper) return;

        wrapper.addEventListener('scroll', handleScroll);
        return () => wrapper.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // Mark messages as read
    useEffect(() => {
        if (id && allMessages.length > 0) {
            const lastMessage = allMessages[allMessages.length - 1];
            if (lastMessage && lastMessage.from !== profile?.uuid) {
                readGroupMessage({ groupId: id, messageId: lastMessage.uuid });
            }
        }
    }, [id, profile, allMessages, readGroupMessage]);

    // Handle sent messages
    const handleMessageSent = useCallback(
        (newMessage) => {
            // Remove optimistic update - just trigger refetch
            setShowScrollButton(false);
            shouldScrollToBottom.current = true;

            // Reset pagination to get latest messages
            setBefore(null);
            refetch();

            // Scroll to bottom after a short delay to ensure new message is rendered
            setTimeout(() => {
                if (bottomRef.current) {
                    bottomRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 200);
        },
        [refetch]
    );

    // Group messages by sender
    const groupMessagesById = useCallback((messages) => {
        if (!messages) return [];

        const groupedMessages = [];
        let currentGroup = [];

        messages.forEach((message, index) => {
            if (
                currentGroup.length === 0 ||
                currentGroup[0].from === message.from
            ) {
                currentGroup.push(message);
            } else {
                groupedMessages.push(currentGroup);
                currentGroup = [message];
            }
        });

        if (currentGroup.length > 0) {
            groupedMessages.push(currentGroup);
        }

        return groupedMessages;
    }, []);

    // Enhanced message list with better date header logic
    const messageList = useMemo(() => {
        const groupedMessages = groupMessagesById(allMessages);

        return groupedMessages?.map((group, groupIndex) => {
            const firstMessage = group[0];
            const previousGroup =
                groupIndex > 0 ? groupedMessages[groupIndex - 1] : null;
            const previousMessage = previousGroup
                ? previousGroup[previousGroup.length - 1]
                : null;

            // Enhanced date header logic - only show if dates are actually different
            const currentDate = new Date(
                firstMessage.created_at
            ).toDateString();
            const previousDate = previousMessage
                ? new Date(previousMessage.created_at).toDateString()
                : null;
            const showDateHeader =
                groupIndex === 0 || currentDate !== previousDate;

            return (
                <React.Fragment key={`group-${groupIndex}`}>
                    {showDateHeader && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                my: 3, // More spacing to prevent overlap
                                width: '100%',
                                position: 'relative'
                            }}
                        >
                            <Box
                                sx={{
                                    backgroundColor:
                                        'rgba(255, 255, 255, 0.95)',
                                    backdropFilter: 'blur(8px)',
                                    borderRadius: '20px',
                                    px: 3,
                                    py: 1,
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                    minWidth: '80px',
                                    textAlign: 'center'
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#374151',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        textTransform: 'capitalize',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    {formatDate(firstMessage.created_at)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <GroupMessageBox groupMessages={group} profile={profile} />
                </React.Fragment>
            );
        });
    }, [allMessages, formatDate, profile, groupMessagesById]);

    // Handle real-time updates - ADD THIS MISSING LOGIC
    const prevAfterRef = useRef(page?.after);
    useEffect(() => {
        if (
            page?.after &&
            prevAfterRef.current &&
            page.after !== prevAfterRef.current
        ) {
            setBefore(null);
        }
        prevAfterRef.current = page?.after;
    }, [page?.after]);

    if (isLoading) {
        return (
            <Box className="flex items-center justify-center h-20 gap-4">
                <Typography
                    sx={{
                        color: 'teal',
                        fontSize: isSmallScreen ? '1rem' : '1.25rem',
                        fontWeight: 'bold'
                    }}
                >
                    Loading...
                </Typography>
                <FiCommand className="text-teal-500 text-2xl animate-spin-fast" />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', gap: '3px' }}>
            <Box sx={{ flex: 1 }}>
                {/* Chat Header */}
                <Box
                    sx={{
                        backgroundColor: '#27485D',
                        height: '64px',
                        display: 'flex',
                        alignItems: 'center',
                        px: '16px',
                        position: 'sticky',
                        top: 0,
                        gap: '8px',
                        borderRadius: '4px 4px 0 0'
                    }}
                >
                    <div
                        className="hover:cursor-pointer flex items-center gap-3"
                        onClick={() => setShowChatDetails(!showChatDetails)}
                    >
                        <UserAvatar
                            imgSource={groupProfile?.image_url}
                            fullName={groupProfile?.name}
                            isActive={'active'}
                        />
                        <Typography
                            sx={{
                                width: '100%',
                                display: 'flex',
                                color: 'white',
                                cursor: 'pointer'
                            }}
                            variant="body2"
                        >
                            {groupProfile?.name}
                        </Typography>
                    </div>
                </Box>

                {/* Chat Content */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'calc(100dvh - 165px)'
                    }}
                >
                    {/* Messages Container */}
                    <Box
                        ref={messagesWrapperRef}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            pt: '12px',
                            overflowY: 'auto',
                            // scrollbarWidth: "thin",
                            height: 'calc(100dvh - 265px)',
                            // Enhanced scrollbar styling
                            '&::-webkit-scrollbar': {
                                width: '8px'
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: '#f1f1f1',
                                borderRadius: '4px'
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#c1c1c1',
                                borderRadius: '4px',
                                '&:hover': {
                                    backgroundColor: '#a1a1a1'
                                }
                            },
                            // For Firefox
                            scrollbarColor: '#c1c1c1 #f1f1f1'
                        }}
                    >
                        {/* Top loader for infinite scroll */}
                        {isLoadingOlderMessages && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    py: 2,
                                    gap: 1
                                }}
                            >
                                <FiCommand className="text-teal-500 text-lg animate-spin" />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'teal',
                                        fontSize: '0.85rem'
                                    }}
                                >
                                    Loading older messages...
                                </Typography>
                            </Box>
                        )}

                        <div ref={topMessageRef} />

                        {allMessages.length > 0 ? (
                            messageList
                        ) : (
                            <Box className="flex justify-center py-20">
                                <Typography>No messages available!</Typography>
                            </Box>
                        )}

                        <div ref={bottomRef} className="w-full h-[1px] mb-2" />
                    </Box>

                    {/* Scroll to Bottom Button */}
                    {showScrollButton && (
                        <Box sx={{ position: 'relative', width: '100%' }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: '50%',
                                    bottom: '8px',
                                    transform: 'translateX(-50%)',
                                    zIndex: 1000
                                }}
                            >
                                <button
                                    className="rounded-full flex justify-center items-center bg-[#2FACFE] w-8 h-8 shadow-lg hover:bg-[#1e8fd4] transition-colors"
                                    onClick={() => {
                                        shouldScrollToBottom.current = true;
                                        scrollToBottom('smooth');
                                        setShowScrollButton(false);
                                    }}
                                >
                                    <FaArrowDown color="white" />
                                </button>
                            </Box>
                        </Box>
                    )}

                    {/* Send Message Area */}
                    <Box>
                        <SendGroupChat onMessageSent={handleMessageSent} />
                    </Box>
                </Box>
            </Box>

            {/* Chat Details Sidebar */}
            {showChatDetails && (
                <Box
                    sx={{
                        height: 'calc(100dvh - 101px)',
                        borderRadius: '8px',
                        width: {
                            xs: '30%', // width for extra-small screens (mobile)
                            sm: '30%', // width for small screens (tablets) 600px and up
                            md: '40%', // width for medium screens (small laptops) 900px and up
                            lg: '50%' // width for large screens (desktops) 1200px and up
                        },
                        maxWidth: '356px',
                        overflow: 'auto',
                        backgroundColor: 'white',
                        margin: '0 auto' // Center the box horizontally
                    }}
                >
                    <GroupChatDetails
                        onClose={() => setShowChatDetails(false)}
                        open={showChatDetails}
                    />
                </Box>
            )}
        </Box>
    );
};

export default GroupChatMessageContent;
